import axios from 'helpers/api';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const MedicationInstructionsModal = ({ show, handleClose, dataObj, splitObj, onSaved }) => {
  const MIN_ROWS = 2;
  const MAX_ROWS = 4;
  const isAddMode = !splitObj?.length;
  const { id } = useParams();
  const [finalData, setData] = useState([]);
  const [frequency] = useState(dataObj?.frequency || []);
  const [amount] = useState(dataObj?.amount || []);
  const [hours] = useState(dataObj?.prescription_hour || []);
  const [activeRow, setActiveRow] = useState(null);
  const [bottomIdx, setBottomIdx] = useState(null);

  const blankRow = (isPRN = false) => ({
    sig: '',
    amount: '',
    dose: '',
    selectedFreq: null,
    hours: ['', '', '', ''],
    med_type: isPRN ? 'P' : 'O'
  });


  useEffect(() => {
    if (!show) return;

    if (isAddMode) {
      setData([
        blankRow(false),
        blankRow(false)
      ]);

      setActiveRow(null);
      return;
    }
    let rows = (splitObj || []).map((p, idx) => {
      const freqObj =
        dataObj?.frequency.find(
          f => Number(f.id) === Number(p.frequency_id)
        ) || null;
      return {
        sr: idx + 1,
        id: p.id,
        sr_id: p.sr_id,
        sig: p.sig,
        amount: p.amount_id ?? '',
        dose: p.dose ?? '',
        selectedFreq: freqObj,
        hours: [
          p.time_1 ?? '',
          p.time_2 ?? '',
          p.time_3 ?? '',
          p.time_4 ?? ''
        ],
        med_type: p.med_type ?? 'O'
      };
    });
    const prnIndex = rows.findIndex(r => r.med_type === 'P');

    if (prnIndex > 0) {
      const [prnRow] = rows.splice(prnIndex, 1);
      rows.unshift(prnRow);
    }

    setData(rows);
    const newPrnIndex = rows.findIndex(r => r.med_type === 'P');
    setActiveRow(newPrnIndex >= 0 ? newPrnIndex : null);
  }, [show, isAddMode, splitObj, dataObj]);

  const handleAddRow = () => {
    setData(prev => {
      if (prev.length >= MAX_ROWS) return prev;

      return [...prev, blankRow(false)];
    });
  };


  const handleInputChange = (index, field, value) => {
    setData((prev) => {
      const next = [...prev];
      next[index][field] = value;
      return next;
    });
  };

  const handleHourChange = (index, hourIndex, value) => {
    setData((prev) => {
      const next = [...prev];
      if (!next[index].hours) next[index].hours = [];
      next[index].hours[hourIndex] = value;
      return next;
    });
  };


  const rowsForRender = useMemo(() => {
    return finalData.map((item, idx) => ({
      item,
      idx
    }));
  }, [finalData]);

  const handleRemoveRow = (index) => {
    setData(prev => {
      if (prev.length <= MIN_ROWS) return prev;

      const updated = prev.filter((_, i) => i !== index);

      return updated;
    });
  };

  const handleTogglePrn = (idx, checked) => {
    setData(prev =>
      prev.map((row, i) => ({
        ...row,
        med_type: checked
          ? (i === idx ? 'P' : 'O')
          : (i === idx ? 'O' : row.med_type)
      }))
    );
    setActiveRow(checked ? idx : null);
  };

  const handleSave = async () => {
    for (let i = 0; i < finalData.length; i++) {
      const item = finalData[i];
      const isPRN =
        String(item.med_type || '').toUpperCase() === 'P' || activeRow === i;

      if (!item.amount) {
        toast.error(`Amount is required for row ${i + 1}`);
        return;
      }

      const doseNum = item.dose;
      if (!doseNum) {
        toast.error(`dose is required for row ${i + 1}`);
        return;
      }

      if (!item.selectedFreq) {
        toast.error(`Frequency is required for row ${i + 1}`);
        return;
      }

      // Only require hours for non-PRN rows
      if (!isPRN) {
        if (!item.hours || item.hours.every(h => !h)) {
          toast.error(`At least one hour must be selected for row ${i + 1}`);
          return;
        }
      }
    }


    const payload = finalData.map((item, idx) => {
      const base = {
        dose: item.dose,
        amount_id: Number(item.amount),
        frequency_id: Number(item.selectedFreq.id),
        time_1: item.hours?.[0] || null,
        time_2: item.hours?.[1] || null,
        time_3: item.hours?.[2] || null,
        time_4: item.hours?.[3] || null,
        prescription_id: Number(id),
        sr_id: item.sr_id ?? idx + 1,
        med_type: item.med_type || null,
        sig: item.sig || ''
      };
      if (item.id) base.id = item.id;
      return base;
    });

    try {
      const tokenStr = localStorage.getItem('accessToken');
      const { data } = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/prescriptions/update-splitmends`,
        payload,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );

      if (data.status) {
        const serverRows = Array.isArray(data.splitmeds) ? data.splitmeds : null;
        const normalized = (serverRows ?? finalData).map((row, idx) => ({
          id: serverRows ? row.id : (row.id ?? undefined),
          sr_id: isAddMode ? idx + 1 : (serverRows ? row.sr_id : row.sr_id),
          sig: serverRows ? (row.sig ?? '') : (row.sig ?? ''),
          amount_id: serverRows ? row.amount_id : row.amount,
          dose: serverRows ? row.dose : Number(row.dose),
          frequency_id: serverRows ? row.frequency_id : row.selectedFreq.id,
          time_1: serverRows ? row.time_1 : (row.hours?.[0] || null),
          time_2: serverRows ? row.time_2 : (row.hours?.[1] || null),
          time_3: serverRows ? row.time_3 : (row.hours?.[2] || null),
          time_4: serverRows ? row.time_4 : (row.hours?.[3] || null),
          med_type: serverRows ? row.med_type : (row.med_type || '')
        }));

        onSaved?.(normalized);
        setData(finalData);
        toast.success(isAddMode ? 'Splitmeds added successfully' : 'Splitmeds updated successfully');
        handleClose();
      } else {
        toast.error('Something went wrong');
      }
    } catch {
      toast.error('Request failed');
    }
  };
  const hasBackendPRN = useMemo(
    () => finalData.some(r => r.med_type === 'P'),
    [finalData]
  );
  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton className="border-bottom-0">
        <Modal.Title className="fw-semibold">
          Splitmeds
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-end mb-2">
          {finalData.length < MAX_ROWS && (
            <Button size="sm" onClick={handleAddRow}>
              + Add Row
            </Button>
          )}
        </div>
        {finalData.length > 0 ? (
          <div className="splitmeds-wrapper">

            {rowsForRender.map(({ item, idx }, renderIdx) => {
              const isBottom = renderIdx === rowsForRender.length - 1;
              const isActive = activeRow === idx;
              const hoursDisabled = isActive;
              return (
                <div
                  key={idx}
                  className="splitmed-row border-bottom border-dark pb-3 mb-3"
                >

                  {/* ===== TOP HEADER ROW ===== */}
                  <div className="d-flex justify-content-between align-items-center mb-2">

                    <Form.Label className="fw-semibold mb-0">
                      Direction
                    </Form.Label>

                    <div className="d-flex align-items-center gap-2">

                      {/* PRN Toggle */}
                      {(
                        (isAddMode && idx === 0) ||
                        (!isAddMode && (
                          item.med_type === 'P' ||
                          (!hasBackendPRN && renderIdx === 0)
                        ))
                      ) && (
                          <Form.Check
                            type="switch"
                            label="PRN"
                            checked={item.med_type === 'P'}
                            onChange={(e) =>
                              handleTogglePrn(idx, e.target.checked)
                            }
                          />
                        )}

                      {/* Remove Row Button */}
                      {idx >= 2 && !item.id && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveRow(idx)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* ===== TEXTAREA ===== */}
                  <Form.Control
                    as="textarea"
                    rows={2}
                    className="mb-3"
                    value={item.sig || ""}
                    placeholder="take 1 tablet (500mg) in the morning"
                    onChange={(e) =>
                      handleInputChange(idx, "sig", e.target.value)
                    }
                  />

                  {/* ===== INPUT ROW ===== */}
                  <div className="row g-3 align-items-end">

                    {/* Amount */}
                    <div className="col-md-1">
                      <Form.Label className="small text-muted">Amount</Form.Label>
                      <Form.Select
                        size="sm"
                        value={item.amount || ""}
                        onChange={(e) =>
                          handleInputChange(idx, "amount", e.target.value)
                        }
                      >
                        <option value="">Select</option>
                        {amount.map(opt => (
                          <option key={opt.id} value={opt.id}>
                            {opt.amount}
                          </option>
                        ))}
                      </Form.Select>
                    </div>

                    {/* Dose */}
                    <div className="col-md-2">
                      <Form.Label className="small text-muted">Dose</Form.Label>
                      <Form.Control
                        size="sm"
                        value={item.dose || ""}
                        onChange={(e) =>
                          handleInputChange(idx, "dose", e.target.value)
                        }
                      />
                    </div>

                    {/* Frequency */}
                    <div className="col-md-4">
                      <Form.Label className="small text-muted">Frequency</Form.Label>

                      <ThemedSelect
                        options={frequency}
                        value={item.selectedFreq}
                        getOptionLabel={(o) => o.name}
                        getOptionValue={(o) => o.id}
                        onChange={(selected) => {
                          const updated = [...finalData];
                          updated[idx].selectedFreq = selected || null;
                          setData(updated);
                        }}
                      />
                    </div>

                    {/* Hours */}
                    <div className="col-md-5">
                      <Form.Label className="small text-muted">
                        Hours {hoursDisabled && "(not required for PRN)"}
                      </Form.Label>

                      <div className="d-flex gap-2">
                        {[0, 1, 2, 3].map(hourIndex => (
                          <Form.Select
                            key={hourIndex}
                            size="sm"
                            disabled={hoursDisabled}
                            value={item.hours?.[hourIndex] || ""}
                            onChange={(e) =>
                              handleHourChange(idx, hourIndex, e.target.value)
                            }
                          >
                            <option value="">--</option>
                            {hours.map(opt => (
                              <option key={opt.id} value={opt.id}>
                                {opt.time}
                              </option>
                            ))}
                          </Form.Select>
                        ))}
                      </div>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-muted">
            No medication data found.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="success" onClick={handleSave}>
          Save
        </Button>
        <Button
          variant="outline-secondary"
          onClick={() => {
            setData([]);
            handleClose();
          }}
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MedicationInstructionsModal;

